import { useEffect, useState } from 'react';
import type { SetBudgetEstimatesCommand } from '@/api/generated';
import { useBudgetEstimates, useSetBudgetEstimates } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Input } from '@/design-system/primitives/Input/Input';
import { BUDGET_POSTS } from '@/domain/budgetPosts';
import { formatNumberNlBE, toNumber } from '@/lib/format';
import styles from './BudgetEstimatesDialog.module.css';

// Parse a euro string ("158000" or "1200,50") to a number; "" / invalid → 0.
function parseEuro(value: string): number {
  const n = Number(value.trim().replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function BudgetEstimatesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const estimates = useBudgetEstimates();
  const setMutation = useSetBudgetEstimates();

  const [values, setValues] = useState<Record<string, string>>({});
  // Rebuild the editable values from the server state every time the dialog
  // opens (and if the data refetches while open). Keying on `open` — rather
  // than guarding on a non-empty list — means reopening after clearing all
  // estimates correctly shows empty inputs instead of stale typed-in strings.
  useEffect(() => {
    if (!open) return;
    const map: Record<string, string> = {};
    for (const e of estimates.data ?? []) map[e.post] = String(toNumber(e.expected));
    setValues(map);
  }, [open, estimates.data]);

  const sum = BUDGET_POSTS.reduce((s, p) => s + parseEuro(values[p.id] ?? ''), 0);

  const onSave = async () => {
    const body: SetBudgetEstimatesCommand = {
      estimates: BUDGET_POSTS.map((p) => ({
        post: p.id,
        expected: parseEuro(values[p.id] ?? ''),
      })),
    };
    await setMutation.mutateAsync(body);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Verwachte kosten</DialogTitle>
        <DialogDescription>
          Vul de geraamde (offerte)waarde per budgetpost in. Laat leeg of zet op 0 om geen raming te
          bewaren.
        </DialogDescription>
        <DialogBody>
          <div className={styles.list}>
            {BUDGET_POSTS.map((p) => (
              <div key={p.id} className={`row ${styles.row}`}>
                <span className={styles.label}>{p.label}</span>
                <Input
                  className={styles.input}
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  aria-label={p.label}
                  value={values[p.id] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [p.id]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className={`row ${styles.sumRow}`}>
            <span>Totaal geraamd</span>
            <span className={styles.sumValue}>€ {formatNumberNlBE(sum)}</span>
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button">Annuleer</Btn>
          </DialogClose>
          <Btn variant="primary" onClick={onSave} disabled={setMutation.isPending}>
            {setMutation.isPending ? 'Bezig…' : 'Opslaan'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
